export const toValidHex = (val) => {
    if (!val || typeof val !== 'string') return '#000000';
    const trimmed = val.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed;
    if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
        return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`;
    }
    if (trimmed.startsWith('rgba') || trimmed.startsWith('rgb')) {
        const match = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
            const r = Math.min(255, parseInt(match[1], 10)).toString(16).padStart(2, '0');
            const g = Math.min(255, parseInt(match[2], 10)).toString(16).padStart(2, '0');
            const b = Math.min(255, parseInt(match[3], 10)).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        }
    }
    return '#000000';
};

// --- INLINE STYLE CONTROLS ---
export const InlineStyleControls = ({ prefix, path, currentData, onUpdate }) => {
    const val = currentData || {};
    const update = onUpdate || (() => {}); 

    // Common style for consistent height and alignment
    const fieldStyle = {
        height: '34px',
        fontSize: '0.85rem',
        padding: '0 8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        background: 'white',
        boxSizing: 'border-box',
        width: '100%',
        margin: '0',
        lineHeight: 'normal',
        outline: 'none'
    };

    const labelStyle = { 
        fontSize: '0.65rem', 
        fontWeight: 'bold', 
        color: '#64748b', 
        textTransform: 'uppercase', 
        letterSpacing: '0.05em',
        height: '14px',
        lineHeight: '14px',
        display: 'block',
        margin: '0 0 4px 0'
    };

    const columnStyle = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        minWidth: '0'
    };

    const isGradient = val[`${prefix}TextGradientMode`] === 'gradient';
    const isCustomShadow = val[`${prefix}TextShadowPreset`] === 'custom';

    return (
        <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '10px', 
            marginBottom: '20px', 
            background: '#f8fafc', 
            padding: '14px', 
            borderRadius: '8px', 
            border: '1px solid #cbd5e1' 
        }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '12px',
                alignItems: 'end'
            }}>
                <div style={columnStyle}>
                    <label style={labelStyle}>Alignment</label>
                    <select 
                        value={val[`${prefix}Align`] || ''} 
                        onChange={e => update([...path, `${prefix}Align`], e.target.value)} 
                        style={fieldStyle}
                    >
                        <option value="">Default</option>
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                        <option value="justify">Justify</option>
                    </select>
                </div>
                <div style={columnStyle}>
                    <label style={labelStyle}>Weight</label>
                    <select 
                        value={val[`${prefix}FontWeight`] || val[`${prefix}Weight`] || ''} 
                        onChange={e => {
                            const v = e.target.value;
                            update([...path, `${prefix}FontWeight`], v);
                            update([...path, `${prefix}Weight`], v);
                        }} 
                        style={fieldStyle}
                    >
                        <option value="">Default</option>
                        <option value="100">100 Thin</option>
                        <option value="200">200 Extra Light</option>
                        <option value="300">300 Light</option>
                        <option value="400">400 Normal</option>
                        <option value="500">500 Medium</option>
                        <option value="600">600 Semi-Bold</option>
                        <option value="700">700 Bold</option>
                        <option value="800">800 Extra Bold</option>
                        <option value="900">900 Black</option>
                    </select>
                </div>
                <div style={columnStyle}>
                    <label style={labelStyle}>Size</label>
                    <input 
                        placeholder="e.g. 2.5rem" 
                        value={val[`${prefix}Size`] || ''} 
                        onChange={e => update([...path, `${prefix}Size`], e.target.value)} 
                        onBlur={e => {
                            const v = e.target.value;
                            const trimmed = v.trim().toLowerCase();
                            if (trimmed === '0' || trimmed === '0px' || trimmed === '0rem' || trimmed === '0em') {
                                update([...path, `${prefix}Size`], '1px');
                            }
                        }}
                        style={fieldStyle} 
                    />
                </div>
                <div style={columnStyle}>
                    <label style={labelStyle}>Color Mode</label>
                    <select
                        value={val[`${prefix}TextGradientMode`] || 'solid'}
                        onChange={e => update([...path, `${prefix}TextGradientMode`], e.target.value)}
                        style={fieldStyle}
                    >
                        <option value="solid">Solid Color</option>
                        <option value="gradient">Gradient Text</option>
                    </select>
                </div>
                {!isGradient && (
                    <div style={columnStyle}>
                        <label style={labelStyle}>Text Color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '34px' }}>
                            <div style={{ ...fieldStyle, flex: 1, position: 'relative', padding: 0, overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: val[`${prefix}Color`] || '#fff' }}></div>
                                <input type="color" value={toValidHex(val[`${prefix}Color`])} onChange={e => update([...path, `${prefix}Color`], e.target.value)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} title="Choose Color" />
                            </div>
                            <button type="button" onClick={() => update([...path, `${prefix}Color`], '')} title="Clear" style={{ ...fieldStyle, width: '34px', padding: 0, cursor: 'pointer', border: '1px solid #ccc', background: 'white', color: '#ef4444', fontWeight: 'bold', flex: '0 0 34px', justifyContent: 'center' }}>×</button>
                        </div>
                    </div>
                )}
                <div style={columnStyle}>
                    <label style={labelStyle}>Drop Shadow</label>
                    <select
                        value={val[`${prefix}TextShadowPreset`] || 'none'}
                        onChange={e => update([...path, `${prefix}TextShadowPreset`], e.target.value)}
                        style={fieldStyle}
                    >
                        <option value="none">None</option>
                        <option value="subtle">Subtle Shadow</option>
                        <option value="medium">Medium Shadow</option>
                        <option value="hard">Hard Drop</option>
                        <option value="softGlow">Soft White Glow</option>
                        <option value="darkGlow">Dark Glow</option>
                        <option value="custom">Custom CSS...</option>
                    </select>
                </div>
            </div>

            {isCustomShadow && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={labelStyle}>Custom Text Shadow CSS</label>
                    <input
                        value={val[`${prefix}TextShadowCustom`] || ''}
                        onChange={e => update([...path, `${prefix}TextShadowCustom`], e.target.value)}
                        placeholder="e.g. 0 4px 10px rgba(0,0,0,0.7), 0 0 20px #ff007f"
                        style={{ ...fieldStyle, fontFamily: 'monospace' }}
                    />
                </div>
            )}

            {isGradient && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '10px',
                    alignItems: 'end',
                    background: '#ffffff',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1'
                }}>
                    <div style={columnStyle}>
                        <label style={labelStyle}>Gradient Start</label>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <input type="color" value={toValidHex(val[`${prefix}TextGradientStart`])} onChange={e => update([...path, `${prefix}TextGradientStart`], e.target.value)} style={{ width: '34px', height: '34px', padding: 0, border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }} />
                            <input value={val[`${prefix}TextGradientStart`] || '#3b82f6'} onChange={e => update([...path, `${prefix}TextGradientStart`], e.target.value)} style={fieldStyle} placeholder="#3b82f6" />
                        </div>
                    </div>
                    <div style={columnStyle}>
                        <label style={labelStyle}>Gradient End</label>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <input type="color" value={toValidHex(val[`${prefix}TextGradientEnd`])} onChange={e => update([...path, `${prefix}TextGradientEnd`], e.target.value)} style={{ width: '34px', height: '34px', padding: 0, border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }} />
                            <input value={val[`${prefix}TextGradientEnd`] || '#9333ea'} onChange={e => update([...path, `${prefix}TextGradientEnd`], e.target.value)} style={fieldStyle} placeholder="#9333ea" />
                        </div>
                    </div>
                    <div style={columnStyle}>
                        <label style={labelStyle}>Angle (deg)</label>
                        <input
                            type="text"
                            value={val[`${prefix}TextGradientAngle`] || '135'}
                            onChange={e => update([...path, `${prefix}TextGradientAngle`], e.target.value)}
                            placeholder="135"
                            style={fieldStyle}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// --- OPTIMIZED INPUT (Fixes iOS Typing) ---
export const TextInput = ({ value, onChange, placeholder, style }) => {
    const [localValue, setLocalValue] = useState(value || '');
    useEffect(() => { setLocalValue(value || ''); }, [value]);
    const handleBlur = () => { if (localValue !== value) onChange({ target: { value: localValue } }); };
    return <input value={localValue} onChange={(e) => setLocalValue(e.target.value)} onBlur={handleBlur} placeholder={placeholder} style={style} />;
};

// --- Better Color Picker for iOS ---
export const ColorInput = ({ label, value, onChange, allowTransparent = true }) => {
    const isTransparent = value === 'transparent' || value === 'rgba(0,0,0,0)' || value === 'none';
    
    return (
        <div style={{ marginBottom: '10px' }}>
            {label && <label style={{ fontSize: '0.8em', color: '#666', display: 'block', marginBottom: '5px' }}>{label}</label>}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {/* Native Picker (Visual) */}
                <input
                    type="color"
                    value={isTransparent ? '#ffffff' : toValidHex(value)}
                    onChange={(e) => onChange(e.target.value)}
                    style={{ height: '38px', width: '42px', border: '1px solid #ccc', padding: 0, borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}
                    title="Choose Color"
                />
                {/* Text Input (Hex or rgba or transparent) */}
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="#000000 or transparent"
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem', minWidth: 0 }}
                />
                {allowTransparent && (
                    <button
                        type="button"
                        onClick={() => onChange(isTransparent ? '#3b82f6' : 'transparent')}
                        title={isTransparent ? "Set Solid Color" : "Set Transparent"}
                        style={{
                            padding: '6px 10px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            border: isTransparent ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                            borderRadius: '4px',
                            background: isTransparent ? '#eff6ff' : '#f8fafc',
                            color: isTransparent ? '#2563eb' : '#64748b',
                            cursor: 'pointer',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {isTransparent ? '💧 Clear' : '🚫 Clear'}
                    </button>
                )}
            </div>
        </div>
    );
};

import { FONT_OPTIONS, getFontOptions, getFontCategories } from '../config/fontOptions.js';
import { useState as _useState, useRef as _useRef, useEffect as _useEffect } from 'react';

// --- RGBA helpers ---
const parseRgba = (str) => {
    if (!str || typeof str !== 'string') return { r: 0, g: 0, b: 0, a: 1 };
    const s = str.trim().toLowerCase();
    if (s === 'transparent' || s === 'none') {
        return { r: 0, g: 0, b: 0, a: 0 };
    }
    const rgbaMatch = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
    if (rgbaMatch) {
        return {
            r: parseInt(rgbaMatch[1], 10),
            g: parseInt(rgbaMatch[2], 10),
            b: parseInt(rgbaMatch[3], 10),
            a: rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1,
        };
    }
    const hexMatch = s.match(/^#([0-9a-f]{6,8})$/);
    if (hexMatch) {
        const h = hexMatch[1];
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
            a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
        };
    }
    const hex3 = s.match(/^#([0-9a-f]{3})$/);
    if (hex3) {
        const h = hex3[1];
        return { r: parseInt(h[0]+h[0], 16), g: parseInt(h[1]+h[1], 16), b: parseInt(h[2]+h[2], 16), a: 1 };
    }
    return { r: 0, g: 0, b: 0, a: 1 };
};

const rgbaToString = (r, g, b, a) => {
    if (a <= 0) return 'transparent';
    return a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${Math.round(a * 100) / 100})`;
};

const rgbaToHex = (r, g, b) =>
    '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');

/**
 * RgbaColorPicker — enhanced inline RGBA color picker
 * Props: label, value (any CSS color string), onChange(string), defaultVal
 */
export const RgbaColorPicker = ({ label, value, onChange, defaultVal = '#000000' }) => {
    const [textVal, setTextVal] = _useState(value || defaultVal);
    const [isFocused, setIsFocused] = _useState(false);
    const [alphaInputVal, setAlphaInputVal] = _useState('');
    const [isAlphaFocused, setIsAlphaFocused] = _useState(false);

    const parsed = parseRgba(value || defaultVal);
    const { r, g, b, a } = parsed;
    const hexColor = rgbaToHex(r, g, b);
    const displayColor = value || defaultVal;
    const alphaPercent = Math.round(a * 100);

    _useEffect(() => {
        if (!isFocused) {
            setTextVal(value || defaultVal);
        }
    }, [value, defaultVal, isFocused]);

    _useEffect(() => {
        if (!isAlphaFocused) {
            setAlphaInputVal(String(alphaPercent));
        }
    }, [alphaPercent, isAlphaFocused]);

    const handleColorChange = (e) => {
        const hex = e.target.value;
        const np = parseRgba(hex);
        const out = rgbaToString(np.r, np.g, np.b, a);
        setTextVal(out);
        onChange(out);
    };

    const handleAlphaSliderChange = (e) => {
        const newA = parseFloat(e.target.value) / 100;
        const out = rgbaToString(r, g, b, newA);
        setTextVal(out);
        setAlphaInputVal(String(Math.round(newA * 100)));
        onChange(out);
    };

    const handleAlphaNumberChange = (e) => {
        const raw = e.target.value;
        setAlphaInputVal(raw);
        if (raw === '') return;
        let valNum = parseFloat(raw);
        if (isNaN(valNum)) return;
        valNum = Math.max(0, Math.min(100, valNum));
        const newA = valNum / 100;
        const out = rgbaToString(r, g, b, newA);
        setTextVal(out);
        onChange(out);
    };

    const handleAlphaNumberBlur = () => {
        setIsAlphaFocused(false);
        let valNum = parseFloat(alphaInputVal);
        if (isNaN(valNum) || alphaInputVal === '') {
            valNum = alphaPercent;
        }
        valNum = Math.max(0, Math.min(100, valNum));
        setAlphaInputVal(String(valNum));
        const newA = valNum / 100;
        const out = rgbaToString(r, g, b, newA);
        if (out !== value) {
            onChange(out);
        }
    };

    const handleTextChange = (e) => {
        const newText = e.target.value;
        setTextVal(newText);
        const trimmed = newText.trim().toLowerCase();
        if (trimmed === 'transparent' || 
            trimmed === 'inherit' ||
            /^#[0-9a-f]{3,8}$/.test(trimmed) || 
            /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+/.test(trimmed)) {
            onChange(newText);
        }
    };

    const handleTextBlur = () => {
        setIsFocused(false);
        if (textVal !== value) {
            onChange(textVal || defaultVal);
        }
    };

    const handleTextFocus = () => {
        setIsFocused(true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    // Checkerboard pattern for alpha transparency display
    const checkerboard = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Crect width='4' height='4' fill='%23ccc'/%3E%3Crect x='4' y='4' width='4' height='4' fill='%23ccc'/%3E%3C/svg%3E")`;

        return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '130px' }}>
            <style>{`
                .rgba-slider-input {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 100%;
                    height: 22px;
                    background: transparent;
                    cursor: pointer;
                    margin: 0;
                    padding: 0;
                }
                .rgba-slider-input::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: #ffffff;
                    border: 2px solid #2563eb;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
                    cursor: pointer;
                    transition: transform 0.15s ease, background-color 0.15s ease;
                }
                .rgba-slider-input::-webkit-slider-thumb:hover {
                    transform: scale(1.15);
                    background: #eff6ff;
                }
                .rgba-slider-input::-moz-range-thumb {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: #ffffff;
                    border: 2px solid #2563eb;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
                    cursor: pointer;
                    transition: transform 0.15s ease, background-color 0.15s ease;
                }
                .rgba-slider-input::-moz-range-thumb:hover {
                    transform: scale(1.15);
                    background: #eff6ff;
                }
            `}</style>
            {label && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                    <span style={{ fontSize: '0.68rem', fontWeight: '600', color: '#475569' }}>{label}</span>
                    <div style={{ display: 'flex', gap: '3px' }}>
                        <button
                            type="button"
                            onClick={() => {
                                const out = rgbaToString(r, g, b, 1);
                                setTextVal(out);
                                setAlphaInputVal('100');
                                onChange(out);
                            }}
                            title="Set solid (100% opacity)"
                            style={{
                                padding: '1px 4px',
                                fontSize: '0.58rem',
                                borderRadius: '3px',
                                border: alphaPercent === 100 ? '1px solid #2563eb' : '1px solid #e2e8f0',
                                background: alphaPercent === 100 ? '#eff6ff' : '#ffffff',
                                color: alphaPercent === 100 ? '#2563eb' : '#64748b',
                                cursor: 'pointer',
                                lineHeight: '1'
                            }}
                        >
                            100%
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const out = 'transparent';
                                setTextVal(out);
                                setAlphaInputVal('0');
                                onChange(out);
                            }}
                            title="Set transparent (0% opacity)"
                            style={{
                                padding: '1px 4px',
                                fontSize: '0.58rem',
                                borderRadius: '3px',
                                border: alphaPercent === 0 ? '1px solid #2563eb' : '1px solid #e2e8f0',
                                background: alphaPercent === 0 ? '#eff6ff' : '#ffffff',
                                color: alphaPercent === 0 ? '#2563eb' : '#64748b',
                                cursor: 'pointer',
                                lineHeight: '1'
                            }}
                        >
                            0%
                        </button>
                    </div>
                </div>
            )}
            {/* Top Row: Color Chip + Direct Text/Hex Input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Color Swatch (clickable native picker) */}
                <div style={{ position: 'relative', width: '32px', height: '28px', borderRadius: '4px', border: '1px solid #cbd5e1', overflow: 'hidden', flexShrink: 0, background: checkerboard, backgroundSize: '8px 8px', boxShadow: 'inset 0 0 2px rgba(0,0,0,0.15)' }}>
                    <div style={{ position: 'absolute', inset: 0, background: displayColor, borderRadius: '3px' }} />
                    <input
                        type="color"
                        value={hexColor}
                        onChange={handleColorChange}
                        title="Click to pick color"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0, padding: 0 }}
                    />
                </div>
                {/* Editable text input for hex / rgba / name */}
                <input
                    type="text"
                    value={textVal}
                    onChange={handleTextChange}
                    onBlur={handleTextBlur}
                    onFocus={handleTextFocus}
                    onKeyDown={handleKeyDown}
                    data-rgba-text="true"
                    placeholder={defaultVal}
                    title="Type hex (#ffffff), rgba(r,g,b,a), or transparent"
                    style={{ 
                        flex: 1, 
                        minWidth: 0,
                        height: '28px',
                        padding: '3px 6px', 
                        borderRadius: '4px', 
                        border: isFocused ? '1px solid #2563eb' : '1px solid #cbd5e1', 
                        fontSize: '0.72rem', 
                        fontFamily: 'monospace', 
                        color: '#1e293b', 
                        background: '#ffffff', 
                        boxSizing: 'border-box',
                        outline: 'none'
                    }}
                />
            </div>
            {/* Bottom Row (UNDER the chip): Full-Width Opacity Slider + Numeric Input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', minWidth: 0, marginTop: '2px' }}>
                <span style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Opacity:
                </span>
                <div style={{ 
                    flex: 1, 
                    position: 'relative', 
                    height: '22px', 
                    borderRadius: '11px', 
                    background: `linear-gradient(to right, transparent, ${hexColor}), ${checkerboard}`, 
                    backgroundSize: 'auto, 8px 8px', 
                    border: '1px solid #cbd5e1',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                    padding: '0 2px',
                    minWidth: 0
                }}>
                    <input
                        type="range"
                        className="rgba-slider-input"
                        min="0"
                        max="100"
                        value={alphaPercent}
                        onChange={handleAlphaSliderChange}
                        title={`Drag opacity slider (${alphaPercent}%)`}
                    />
                </div>
                {/* Direct numeric input for opacity percentage */}
                <div style={{ display: 'inline-flex', alignItems: 'center', background: '#ffffff', border: isAlphaFocused ? '1px solid #2563eb' : '1px solid #cbd5e1', borderRadius: '4px', padding: '1px 3px', height: '22px', boxSizing: 'border-box', flexShrink: 0 }}>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        value={alphaInputVal}
                        onFocus={() => setIsAlphaFocused(true)}
                        onBlur={handleAlphaNumberBlur}
                        onChange={handleAlphaNumberChange}
                        title="Directly type opacity (0-100)"
                        style={{ 
                            width: '28px', 
                            border: 'none', 
                            outline: 'none', 
                            fontSize: '0.68rem', 
                            fontFamily: 'monospace', 
                            fontWeight: '700', 
                            color: '#1e293b', 
                            textAlign: 'right', 
                            padding: 0,
                            background: 'transparent'
                        }}
                    />
                    <span style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 'bold', marginLeft: '1px' }}>%</span>
                </div>
            </div>
        </div>
    );
};

/**
 * Standardized reusable FontFamilySelect dropdown
 */
export const FontFamilySelect = ({ value, onChange, style = {}, includeInherit = true, customFonts = [] }) => {
    const currentValue = value || 'inherit';
    const categories = getFontCategories(customFonts);
    const allOptions = getFontOptions(customFonts, false);
    
    return (
        <select
            value={currentValue}
            onChange={onChange}
            style={{
                width: '100%',
                padding: '4px 6px',
                marginTop: '2px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '0.75rem',
                background: '#ffffff',
                color: '#334155',
                cursor: 'pointer',
                boxSizing: 'border-box',
                outline: 'none',
                ...style
            }}
        >
            {includeInherit && <option value="inherit">Inherit (Default)</option>}
            {categories.map(cat => {
                const groupFonts = allOptions.filter(f => f.category === cat);
                if (groupFonts.length === 0) return null;
                return (
                    <optgroup key={cat} label={cat}>
                        {groupFonts.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                    </optgroup>
                );
            })}
        </select>
    );
};

/**
 * Reusable BorderControl with synchronized color picker & text CSS string
 */
export const extractBorderColor = (borderStr, fallback = '#cbd5e1') => {
    if (!borderStr || typeof borderStr !== 'string') return fallback;
    const str = borderStr.trim();
    if (str === 'none' || str === 'transparent' || str === '') return fallback;
    
    const hexMatch = str.match(/#[0-9a-fA-F]{3,8}/);
    if (hexMatch) {
        let hex = hexMatch[0];
        if (hex.length === 4) {
            hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
        }
        return hex.slice(0, 7);
    }
    
    const rgbMatch = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
        const r = Math.min(255, parseInt(rgbMatch[1], 10)).toString(16).padStart(2, '0');
        const g = Math.min(255, parseInt(rgbMatch[2], 10)).toString(16).padStart(2, '0');
        const b = Math.min(255, parseInt(rgbMatch[3], 10)).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    }
    return fallback;
};

export const applyBorderColor = (borderStr, newColor, defaultWidth = '1px', defaultStyle = 'solid') => {
    if (!borderStr || typeof borderStr !== 'string' || borderStr === 'none' || borderStr === 'transparent' || borderStr.trim() === '') {
        return `${defaultWidth} ${defaultStyle} ${newColor}`;
    }
    const str = borderStr.trim();
    if (/#[0-9a-fA-F]{3,8}/.test(str)) {
        return str.replace(/#[0-9a-fA-F]{3,8}/, newColor);
    }
    if (/rgba?\([^)]+\)/.test(str)) {
        return str.replace(/rgba?\([^)]+\)/, newColor);
    }
    const parts = str.split(/\s+/);
    if (parts.length === 1) {
        return `${parts[0]} ${defaultStyle} ${newColor}`;
    } else if (parts.length === 2) {
        return `${parts[0]} ${parts[1]} ${newColor}`;
    } else {
        parts[parts.length - 1] = newColor;
        return parts.join(' ');
    }
};

export const BorderControl = ({ label, value, onChange, placeholder = '1px solid #cbd5e1', style = {}, inputStyle = {} }) => {
    const val = value ?? '';
    const currentColor = extractBorderColor(val, '#cbd5e1');

    const handleColorChange = (e) => {
        const newColor = e.target.value;
        const newBorder = applyBorderColor(val, newColor);
        onChange(newBorder);
    };

    return (
        <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#475569', display: 'flex', flexDirection: 'column', ...style }}>
            {label && <span>{label}</span>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', marginTop: '2px' }}>
                <input
                    type="color"
                    value={currentColor}
                    onChange={handleColorChange}
                    title="Choose border color"
                    style={{
                        width: '28px',
                        height: '28px',
                        padding: '1px',
                        borderRadius: '3px',
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer',
                        flexShrink: 0,
                        background: '#ffffff',
                        boxSizing: 'border-box'
                    }}
                />
                <input
                    type="text"
                    value={val}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        padding: '4px 6px',
                        borderRadius: '3px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.75rem',
                        boxSizing: 'border-box',
                        height: '28px',
                        ...inputStyle
                    }}
                />
            </div>
        </label>
    );
};


